import * as XLSX from 'xlsx';
import React, { useEffect, useState } from 'react';
import TopContent from '../../components/TopContent';
import Table from '../../components/Table';
import { TableHeaderToUserProperty, TableHeaders } from './consts';
import { csvHeaderAdapt, formatDataToTable } from './utils';
import { NEW_USER_ROUTE } from '../../../utils/routes';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../services/store';
import { fetchUsers } from '../../../services/login/loginSlice';
import { useNavigate } from 'react-router-dom';
import { User } from '../../../types/user';

const UsersContent = ({ users }: { users: User[] }) => {
  const isLoading = useSelector((state: RootState) => state.login.isLoading);
  const navigate = useNavigate();

  const DisplayConfig = {
    filtersButton: false,
    searchBar: true,
    addButton: true,
    downloadButton: true,
    editButton: false,
  };

  let filteredUsers = users;
  const [searchText, setSearchText] = useState<string>('');

  const handleSearch = (value: string) => {
    setSearchText(value.trim().toLowerCase());
  };

  if (searchText) {
    const searchFilteredUsers = users.filter((user) => {
      const userAttributesToSearch = Object.keys(TableHeaderToUserProperty)
        .map((key) => user[TableHeaderToUserProperty[key as keyof typeof TableHeaderToUserProperty]])
        .filter((value): value is string => typeof value === 'string');

      const match = userAttributesToSearch.some((attr) => {
        if (typeof attr === 'string') {
          return attr.toLowerCase().includes(searchText);
        }
        return false;
      });

      return match;
    });
    filteredUsers = filteredUsers.length > 0 ? filteredUsers.filter((user) => searchFilteredUsers.includes(user)) : searchFilteredUsers;
  }

  const downloadAsCSV = () => {
    const allKeys: string[] =
      filteredUsers.length > 0
        ? filteredUsers.reduce((keys: string[], user) => {
            Object.keys(user).forEach((key) => {
              if (!keys.includes(key) && key !== '__v' && key !== 'password') {
                keys.push(key);
              }
            });
            return keys;
          }, [])
        : [];
    const headers = allKeys.map(csvHeaderAdapt).join(',') + '\n';
    const content = filteredUsers.map((user) => allKeys.map((key) => (user as any)[key] || '-').join(',')).join('\n');
    const csvContent = headers + content;
    const encodedUri = encodeURI(`data:text/csv;charset=utf-8,${csvContent}`);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'usuarios.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAsExcel = () => {
    const allKeys: string[] =
      filteredUsers.length > 0
        ? filteredUsers.reduce((keys: string[], user) => {
            Object.keys(user).forEach((key) => {
              if (!keys.includes(key) && key !== '__v' && key !== 'password') {
                keys.push(key);
              }
            });
            return keys;
          }, [])
        : [];
    const translatedHeaders = allKeys.map(csvHeaderAdapt);
    const data = filteredUsers.map((user) => allKeys.map((key) => (user as any)[key] || '-'));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([translatedHeaders, ...data]);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(excelBlob);
    link.download = 'usuarios.xlsx';

    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleRowClick = (userId: string) => {
    navigate(`/usuarios/${userId}`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <TopContent
        title={'Usuarios Internos'}
        displayElements={DisplayConfig}
        onClickOpenFilters={() => null}
        onChangeSearchBar={handleSearch}
        newElementRoute={NEW_USER_ROUTE}
        onClickDownload={downloadAsCSV}
        onClickDownloadExcel={downloadAsExcel}
      />
      <div className="userTable wrapperTable">
        <Table
          headers={Object.values(TableHeaders).filter((header) => header !== TableHeaders.ID)}
          data={formatDataToTable(filteredUsers)}
          onRowClick={handleRowClick}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

const Users = () => {
  const token = localStorage.getItem('token');
  const dispatch = useDispatch<AppDispatch>();

  const users = useSelector((state: RootState) => state.login.users);

  useEffect(() => {
    users.length === 0 && dispatch(fetchUsers(token || ''));
  }, []);

  return <UsersContent users={users} />;
};

export default Users;
